import React, { useState, useEffect } from 'react';
import {
  describeBankLoadFailure, bankPickerPlaceholder, describeBankListNotice,
  pledgeBankKeyOf, settleBankAccount, describePledgeLock,
} from './bankPickerAccess';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity, faMoneyCheckAlt, faCalendarAlt, faShip } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import ReportingMonthInput from '../../components/Invoicing/ReportingMonthInput';
import SearchableSelect from '../../components/Invoicing/SearchableSelect';
import InvoiceNotesField from '../../components/Invoicing/InvoiceNotesField';
import { bankAccountLabel } from '../../utils/bankLabel';
import './OutgoingInvoice.css';
import { costCentreLabel } from '../../utils/costCentreLabel';

/**
 * Screen 3: Bank Details, Cost Centre, Notes & Workflow
 * 
 * Fields:
 * - Bank Account Details (dropdown from HMS entity banking)
 * - Cost Centre (dropdown from accounting)
 * - Reporting Month (calendar month-year)
 * - Invoice Notes (rich text)
 * - Notes Visible on Invoice (checkbox)
 * 
 * Note: Save/Workflow buttons are now in the parent orchestrator header
 */

const ProformaInvoiceScreen3 = ({ 
  invoiceData, 
  setInvoiceData,
  lineItems,
  vesselInfo,
  onPrevious,
  invoiceStatus,
  isReadOnly = false
}) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const costCenterParams = invoiceData.hms_entity ? { entity: invoiceData.hms_entity } : {};
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  // Why the list is empty, when it is. See ./bankPickerAccess.js — a
  // refusal and a genuinely empty list used to render identically.
  const [bankLoadFailure, setBankLoadFailure] = useState(null);
  // Pledged: the list holds only the pledge bank's accounts, and this says so.
  const [pledgeLock, setPledgeLock] = useState(null);
  const pledgeKey = pledgeBankKeyOf(invoiceData);
  
  useEffect(() => {
    if (invoiceData.hms_entity) {
      loadBankingDetails();
    } else {
      setBankAccounts([]);
    }
  }, [invoiceData.hms_entity, pledgeKey]);
  
  const loadBankingDetails = async () => {
    if (!invoiceData.hms_entity) {
      setBankAccounts([]);
      return;
    }
    
    setLoadingBankAccounts(true);
    try {
      const response = await invoicingService.fetchBankingDetails(
        'PROFORMA',
        invoiceData.hms_entity,
        null,
        pledgeKey
      );
      
      const banks = response.banking_details || [];
      setBankAccounts(banks);
      setBankLoadFailure(describeBankListNotice(response));
      
      // Fill the box only when the server says there is nothing to guess (the
      // one main account, or the only one). See ./bankPickerAccess.js.
      // Pledged, a choice at another bank is replaced by the pick among
      // the pledge bank's accounts, or emptied (settleBankAccount).
      const settled = settleBankAccount(response, invoiceData.bank_account, pledgeKey);
      if (settled !== invoiceData.bank_account) {
        setInvoiceData(prev => ({ ...prev, bank_account: settled }));
      }
      setPledgeLock(describePledgeLock(response));
      
      console.log(`Loaded ${banks.length} banking details for HMS entity ${invoiceData.hms_entity}`);
    } catch (error) {
      console.error('Error loading banking details:', error);
      setBankAccounts([]);
      setBankLoadFailure(describeBankLoadFailure(error));
    } finally {
      setLoadingBankAccounts(false);
    }
  };
  
  return (
    <>
      <div className="invoice-screen-content">
        <div className="invoice-form-section">
          <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <FontAwesomeIcon icon={faUniversity} /> Bank Details, Cost Centre & Notes
            </span>
            <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
              {invoiceData.invoice_number ? `Pro-Forma Invoice: ${invoiceData.invoice_number}` : '(number will be generated on save)'}
            </span>
          </h2>
          
          <div className="invoice-form-grid">
            {/* Left Column - All Fields */}
            <div className="invoice-form-column">
              {/* Bank Account Details */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faUniversity} /> Bank Account Details
                  {loadingBankAccounts && <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#888' }}>(Loading...)</span>}
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {(() => {
                      const selectedBank = bankAccounts.find(b => b.id === invoiceData.bank_account);
                      if (!selectedBank) return 'N/A';
                      return bankAccountLabel(selectedBank);
                    })()}
                  </div>
                ) : (
                  <select
                    id="bank_account_select"
                    className="form-select"
                    value={invoiceData.bank_account || ''}
                    onChange={(e) => {
                      setInvoiceData(prev => ({ ...prev, bank_account: e.target.value }));
                      clearFieldError('bank_account_select');
                    }}
                    disabled={loadingBankAccounts || !invoiceData.hms_entity}
                  >
                    <option value="">
                      {bankPickerPlaceholder({
                    hasParent: Boolean(invoiceData.hms_entity),
                    parentLabel: 'IMT Entity',
                    withheld: Boolean(bankLoadFailure?.withheld),
                    failed: Boolean(bankLoadFailure && !bankLoadFailure.withheld && !bankLoadFailure.notice),
                    count: bankAccounts.length,
                  })}
                    </option>
                    {bankAccounts.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bankAccountLabel(bank)}
                        {bank.is_primary_bank ? ' (Primary)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Cost Centre */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faMoneyCheckAlt} /> Cost Centre
                </label>
                <SearchableSelect
                  fetchUrl="/api/invoicing/cost-centers/"
                  params={costCenterParams}
                  displayFn={costCentreLabel}
                  value={invoiceData.cost_center || ''}
                  onChange={(id) => {
                    setInvoiceData(prev => ({ ...prev, cost_center: id || '' }));
                    clearFieldError('cost_center_select');
                  }}
                  placeholder="Select Cost Center..."
                  disabled={isReadOnly}
                  variant="form"
                  id="cost_center_select"
                  listOnOpen
                  pageSize={50}
                />
              </div>
              
              {/* Cost Category */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faMoneyCheckAlt} /> Cost Category
                </label>
                <SearchableSelect
                  fetchUrl="/api/invoicing/cost-categories/"
                  displayFn={(cat) => `${cat.code} - ${cat.short_name}`}
                  value={invoiceData.cost_category || ''}
                  onChange={(id) => {
                    setInvoiceData(prev => ({ ...prev, cost_category: id || '' }));
                    clearFieldError('cost_category_select');
                  }}
                  placeholder="Select Cost Category..."
                  disabled={isReadOnly}
                  variant="form"
                  id="cost_category_select"
                  listOnOpen
                  pageSize={50}
                />
              </div>
              
              {/* Reporting Month */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faCalendarAlt} /> Reporting Month
                </label>
                <ReportingMonthInput
                  id="reporting_month_input"
                  className="form-input"
                  value={invoiceData.reporting_month || ''}
                  entityId={invoiceData.hms_entity}
                  onChange={(value) => {
                    setInvoiceData(prev => ({ ...prev, reporting_month: value }));
                  }}
                  onClear={() => clearFieldError('reporting_month_input')}
                  disabled={isReadOnly}
                />
              </div>
            {/* Why the picker is unusable, in words, on the screen. An empty
                dropdown alone reads as "this entity has no bank accounts" and
                sends someone hunting for data that was simply withheld. */}
            {pledgeLock && (
              <p className="field-hint" style={{ marginTop: '6px', fontSize: '0.85em' }}>{pledgeLock}</p>
            )}
            {bankLoadFailure && (
              <p
                className="field-hint"
                role={bankLoadFailure.withheld || bankLoadFailure.notice ? undefined : 'alert'}
                style={{
                  marginTop: '6px',
                  fontSize: '0.85em',
                  color: bankLoadFailure.withheld || bankLoadFailure.notice ? '#5a6b78' : '#a62a20',
                }}
              >
                {bankLoadFailure.message}
              </p>
            )}
            </div>
            
            {/* Right Column - Notes Full Height */}
            <div className="invoice-form-column">
              <InvoiceNotesField
                id="invoice_notes"
                label="Invoice Notes"
                value={invoiceData.notes}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter invoice notes..."
                isReadOnly={isReadOnly}
                fillHeight
                visibilityLabel="Notes visible on invoice (prints as a separate last page)"
                visibilityValue={invoiceData.notes_visible_on_invoice}
                onVisibilityChange={(e) => setInvoiceData(prev => ({ ...prev, notes_visible_on_invoice: e.target.checked }))}
              />
            </div>
          </div>
          
          {/* Navigation - Previous button only */}
          <div className="invoice-workflow-buttons">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onPrevious}
            >
              <FontAwesomeIcon icon={faShip} /> Previous: Vessel & Products
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProformaInvoiceScreen3;
